/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import { Image, Text, FlexGrid } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons/faFile';

import InputWithLabel from '../input-with-header';
import Modal from '../modal';
import BpDateRangePicker from '../bp-date-range-picker';
import Button from '../button';
import ComparisonMetricPropType from '../../utils/prop-types/comparison-metric';
import ComparisonMetricSelector from '../comparison-metric-selector';
import EnsureDelivery from '../ensure-delivery';
import EmailChipList from '../email-chip-list';

import styles from './styles.css';

const { canExportPDF } = require('../../../lib/permissions').default;

export default function BuildReportButton({
  className,
  onGenerateReport,
  gTagTitle,
  comparisonMetric: comparisonMetricProps,
  hideComparisonSelector,
  hideDateRangeSelector,
}) {
  const [emails, setEmails] = React.useState([]);
  const [comments, setComments] = React.useState('');
  const [comparisonMetric, setComparisonMetric] = React.useState(comparisonMetricProps);
  const [emailsError, setEmailsError] = React.useState('');

  const handleOnSave = () => {
    if (!emails.length) {
      setEmailsError('Please enter at least one recipient');
      return false;
    }
    onGenerateReport(emails, comments, comparisonMetric?.dateRange?.startDate, comparisonMetric?.dateRange?.endDate, comparisonMetric);
    return true;
  };

  const handleOnModalClose = () => {
    setEmails([]);
    setComments('');
  };

  const handleEmailInputChange = (e) => {
    setEmails(e);
    if (emailsError.length) {
      setEmailsError('');
    }
  };

  const handleOnCommentsInputChange = (e) => {
    setComments(e.currentTarget.value);
  };

  const handleOnChangeComparisonMetric = (compMetric) => {
    setComparisonMetric({ ...(comparisonMetric || {}), ...compMetric });
  };

  const handleOnChangeDateRange = (dateRange) => {
    setComparisonMetric({ ...(comparisonMetric || {}),
      ...{
        dateRange: {
          startDate: dateRange.startDate.format('YYYY-MM-DD'),
          endDate: dateRange.endDate.format('YYYY-MM-DD'),
        },
      } });
  };

  return (
    canExportPDF() && (
    <Modal
      className={className}
      title="Build Report"
      headerSize="h3"
      saveButtonText="Generate Report"
      onSave={handleOnSave}
      onClose={handleOnModalClose}
      button={(
        <Button type="button" rounded tertiary>
          <FontAwesomeIcon className={styles.buildReportButtonIcon} icon={faFile} />
            Build Report
        </Button>
      )}
      gTagTitle={gTagTitle}
    >
      <Image className={styles.image} src="/images/app/generate_report.png" alt="Build Report" />
      <Text centered>
          We&apos;re just getting things ready but first, let us know which email you want your report sent to,
          and we&apos;ll ship it off to you when it&apos;s ready!
      </Text>
      <FlexGrid container>
        {!hideComparisonSelector ? (
          <FlexGrid.Column desktop={!hideDateRangeSelector ? 6 : 12} mobile={12} tablet={12} className={styles.paddingBottom}>
            <div className={styles.dateRangeLabel}>
            Compare To
              <span className={styles.requiredStar}> *</span>
            </div>
            <ComparisonMetricSelector
              id="forBuildReport"
              key="forBuildReport"
              showBrandMetric
              doNotDispatchChanges
              onChange={handleOnChangeComparisonMetric}
              gTagTitle={gTagTitle}
            />
          </FlexGrid.Column>
        ) : null}
        {!hideDateRangeSelector ? (
          <FlexGrid.Column desktop={!hideComparisonSelector ? 6 : 12} mobile={12} tablet={12} className={styles.paddingBottom}>
            <div className={styles.dateRangeLabel}>
              Date Range
              <span className={styles.requiredStar}> *</span>
            </div>
            <BpDateRangePicker
              doNotDispatchChanges
              onChange={handleOnChangeDateRange}
              gTagTitle={gTagTitle}
            />
          </FlexGrid.Column>
        ) : null}
        <FlexGrid.Column desktop={12} mobile={12} tablet={12}>
          <EmailChipList
            recipients={emails}
            onListChange={handleEmailInputChange}
            error={emailsError}
            onErrorChange={setEmailsError}
          />
        </FlexGrid.Column>
      </FlexGrid>
      <InputWithLabel
        label="Comments"
        onChange={handleOnCommentsInputChange}
        editable
        value={comments}
      />
      <EnsureDelivery />
    </Modal>
    )
  );
}

BuildReportButton.propTypes = {
  className: PropTypes.string,
  gTagTitle: PropTypes.string.isRequired,
  comparisonMetric: ComparisonMetricPropType,
  onGenerateReport: PropTypes.func,
  hideComparisonSelector: PropTypes.bool,
  hideDateRangeSelector: PropTypes.bool,
};

BuildReportButton.defaultProps = {
  className: '',
  onGenerateReport: () => {},
  comparisonMetric: null,
  hideComparisonSelector: false,
  hideDateRangeSelector: false,
};
